import { useMemo, useState } from 'react'
import { STEP_LABELS, type StepId } from '@klear/payable/constants'
import { Button } from '@klear/ui/components/button'
import type { WizardStepGroupNavState } from '@klear/ui/wizard/nav-state'
import { Wizard } from '@klear/ui/wizard/wizard'
import { Check, Pencil } from 'lucide-react'
import { PayableFlow } from '@/app/payable/workflow/payable-flow'
import { isPayableLeaveDirty } from '@/app/payable/workflow/payable-form-state'
import { usePayableWizard, type PayableWizardMode } from '@/app/payable/workflow/usePayableWizard'
import { useWizardDirtyLeaveGuard } from '@klear/ui/wizard/dirty-leave-guard'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'

export type PayableWizardProps = {
  mode: PayableWizardMode
  traderId: number
  traderIdParam: string
  payableId?: number
  orderIdSeed?: number | null
  canEdit?: boolean
  onEdit?: () => void
  onDoneEditing?: () => void
  editable?: boolean
  onBack: () => void
}

export default function PayableWizard({
  mode,
  traderId,
  traderIdParam,
  payableId,
  orderIdSeed,
  canEdit = true,
  onEdit,
  onDoneEditing,
  editable = false,
  onBack
}: PayableWizardProps) {
  const workflow = usePayableWizard({
    mode,
    editable,
    traderId,
    traderIdParam,
    payableId,
    orderIdSeed: orderIdSeed ?? null
  })
  useDocumentTitle(
    workflow.payable?.idDisplay,
    mode === 'create' ? 'New Payable' : mode === 'edit' ? 'Edit Payable' : 'Payable'
  )
  const [nav, setNav] = useState<WizardStepGroupNavState | null>(null)
  const isDirty = !workflow.readOnly && isPayableLeaveDirty(workflow.dirty)
  const { LeaveConfirmDialog, guardExit } = useWizardDirtyLeaveGuard({ when: isDirty })
  const guardedBack = useMemo(() => guardExit(onBack), [guardExit, onBack])
  const guardedDoneEditing = useMemo(
    () => (onDoneEditing ? guardExit(onDoneEditing) : undefined),
    [guardExit, onDoneEditing]
  )

  const activeStepId = nav?.activeStepId ?? ''
  const activeStepLabel = STEP_LABELS[activeStepId as StepId] ?? activeStepId

  const headerActions =
    workflow.readOnly && onEdit && canEdit && workflow.payable?.canEdit ? (
      <Button variant="outline" size="sm" onClick={onEdit}>
        <Pencil className="h-4 w-4 mr-1" />
        Edit
      </Button>
    ) : !workflow.readOnly && guardedDoneEditing ? (
      <Button variant="outline" size="sm" onClick={guardedDoneEditing}>
        <Check className="h-4 w-4 mr-1" />
        Done editing
      </Button>
    ) : null

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {LeaveConfirmDialog}
      <Wizard
        name="Payable"
        subtitle={activeStepLabel}
        steps={nav?.steps ?? []}
        activeStepId={activeStepId}
        onStepSelect={nav?.onStepSelect}
        isStepDisabled={nav?.isStepDisabled}
        onBack={guardedBack}
        backLabel="Exit"
        headerActions={headerActions}
        error={workflow.formError ? <p className="text-sm text-destructive">{workflow.formError}</p> : null}
      >
        <PayableFlow
          workflow={workflow}
          mode={mode}
          payableId={payableId}
          orderIdSeed={orderIdSeed}
          canEdit={canEdit}
          onEdit={onEdit}
          onNavStateChange={setNav}
        />
      </Wizard>
    </div>
  )
}
